import { IntlShape } from 'react-intl';
import Quill from 'quill';

export const mapControlsToQuillToolbar = (controls: string[]) => {
  const toolbarMap: { [key: string]: any } = {
    title: 'title', // Custom title button
    bold: 'bold',
    italic: 'italic',
    strikethrough: 'strike',
    link: 'link',
    numberList: { list: 'ordered' },
    bulletList: { list: 'bullet' },
    emoji: 'emoji', // Custom emoji button
  };

  const toolbar: any[] = [];
  controls.forEach((control) => {
    if (toolbarMap[control] && control !== 'emoji' && control !== 'title') {
      toolbar.push(toolbarMap[control]);
    }
  });

  return toolbar.length > 0 ? [toolbar] : false;
};

export interface FormatDetails {
  formatAction: (quill: Quill, currentActiveState: boolean) => void;
  checkActive: (formats: Record<string, any>) => boolean;
}

export const getFormatDetails = (selector: string, intl: IntlShape): FormatDetails | null => {
  switch (selector) {
    case '.ql-bold':
      return {
        formatAction: (q, isActive) => q.format('bold', !isActive),
        checkActive: (formats) => !!formats.bold,
      };
    case '.ql-italic':
      return {
        formatAction: (q, isActive) => q.format('italic', !isActive),
        checkActive: (formats) => !!formats.italic,
      };
    case '.ql-strike':
      return {
        formatAction: (q, isActive) => q.format('strike', !isActive),
        checkActive: (formats) => !!formats.strike,
      };
    case '.ql-link':
      return {
        formatAction: (q, isActive) => {
          if (isActive) {
            q.format('link', false);
          } else {
            const linkPromptMessage = intl.formatMessage({
              id: 'WYSIWYG.LINK_PROMPT.ENTER_URL',
            });
            const url = prompt(linkPromptMessage);
            if (url) q.format('link', url);
          }
        },
        checkActive: (formats) => !!formats.link,
      };
    case '.ql-list[value="ordered"]':
      return {
        formatAction: (q, isActive) => q.format('list', isActive ? false : 'ordered'),
        checkActive: (formats) => formats.list === 'ordered',
      };
    case '.ql-list[value="bullet"]':
      return {
        formatAction: (q, isActive) => q.format('list', isActive ? false : 'bullet'),
        checkActive: (formats) => formats.list === 'bullet',
      };
    default:
      return null;
  }
};

export const getIconKeyFromSelector = (selector: string): string | null => {
  switch (selector) {
    case '.ql-bold':
      return 'bold';
    case '.ql-italic':
      return 'italic';
    case '.ql-strike':
      return 'strike';
    case '.ql-link':
      return 'link';
    case '.ql-list[value="ordered"]':
      return 'list-ordered';
    case '.ql-list[value="bullet"]':
      return 'list-bullet';
    default:
      return null;
  }
};

export const createButtonContainer = (
  originalButton: HTMLElement,
  className?: string,
): HTMLSpanElement => {
  const spanContainer = document.createElement('span');

  // Copy class for ql-active to work if needed, but override restrictive styles
  spanContainer.className = className || originalButton.className;

  spanContainer.style.display = 'inline-flex';
  spanContainer.style.alignItems = 'center';
  spanContainer.style.justifyContent = 'center';
  spanContainer.style.width = '40px';
  spanContainer.style.height = '40px';
  spanContainer.style.padding = '0';
  spanContainer.style.margin = '0 1px';
  spanContainer.style.border = 'none';
  spanContainer.style.background = 'none';
  spanContainer.style.float = 'none';
  spanContainer.style.verticalAlign = 'top';
  spanContainer.style.boxSizing = 'border-box';

  if (originalButton.hasAttribute('value')) {
    spanContainer.setAttribute('value', originalButton.getAttribute('value')!);
  }

  return spanContainer;
};

export const createCustomButtonWrapper = (className: string): HTMLSpanElement => {
  const wrapper = document.createElement('span');
  wrapper.className = className;
  wrapper.style.display = 'inline-flex';
  wrapper.style.alignItems = 'center';
  wrapper.style.height = '40px';
  wrapper.style.margin = '0 1px';
  wrapper.style.padding = '0';
  wrapper.style.border = 'none';
  wrapper.style.background = 'none';
  wrapper.style.verticalAlign = 'top';
  wrapper.style.boxSizing = 'border-box';

  return wrapper;
};
